package com.crowpay.views.components.project.work.scope

import com.crowpay.Client
import com.crowpay.Contractor
import com.crowpay.Credit
import com.crowpay.HasDisplayName
import com.crowpay.Incident
import com.crowpay.ItemChange
import com.crowpay.LineItem
import com.crowpay.PunchListItem
import com.crowpay.WorkLogItem
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.markAsReadUpdateOnRemove
import com.crowpay.incident
import com.crowpay.isProjectLocked
import com.crowpay.newNotesAllowed
import com.crowpay.newTasksAllowed
import com.crowpay.pending
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.JumpTo
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.mobileButtonRow
import com.crowpay.utils.renderPercent
import com.crowpay.utils.shareFilter
import com.crowpay.views.components.keyMoney
import com.crowpay.views.components.keyString
import com.crowpay.views.components.keyView
import com.crowpay.views.components.label2
import com.crowpay.views.components.labeledSection
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.project.activity.concerns.IncidentReportForm
import com.crowpay.views.components.project.renderContractorNotes
import com.crowpay.views.components.project.work.changeOrders.mobile.ChangeOrderDetails
import com.crowpay.views.components.project.work.punchList.punchListCol
import com.crowpay.views.components.renderPriceChange
import com.crowpay.views.dialogs.CreateOrEditNote
import com.crowpay.views.dialogs.ProjectCreditDetails
import com.crowpay.views.screens.common.AuthorizedProjectView
import com.crowpay.views.screens.common.HasProjectData
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.assertViewerLens
import com.crowpay.views.screens.contractor.MightBeContractorPage
import com.crowpay.views.theming.InnerTabBar
import com.crowpay.views.theming.LightSection
import com.crowpay.views.theming.LineItemStateSemantic
import com.crowpay.views.theming.ListSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.darkSection
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.tertiaryButton
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.DisabledSemantic
import com.lightningkite.kiteui.models.HoverSemantic
import com.lightningkite.kiteui.models.Semantic
import com.lightningkite.kiteui.models.Theme
import com.lightningkite.kiteui.models.ThemeAndBack
import com.lightningkite.kiteui.models.div
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.await
import com.lightningkite.kiteui.reactive.awaitOnce
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.equalTo
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.lens
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEachUpdating
import com.lightningkite.kiteui.views.italic
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.eq
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlin.collections.plus

@Routable("project/{projectId}/work-item/{lineItemId}/{lens}")
class LineItemDetailsPage(
    override val projectId: UUID,
    val lineItemId: UUID,
    override val lens: ProjectLens,
    parent: HasProjectData? = null
) : MightBeContractorPage, HasProjectData by (parent ?: AuthorizedProjectView.Data(lens, projectId)) {
    val lineItem = shared { lineItems().find { it._id == lineItemId } ?: notFoundError<LineItem>() }

    override val title = shared { lineItem().name }

    val tasks = punchListItems.lens { it.filter { it.lineItem == lineItemId } }
    val messages = lineItemCompletionMessages.lens { it.filter { it.lineItem == lineItemId } }
    val draws = payAppItems.lens { it.filter { item -> item.lineItem == lineItemId } }
    val hasPendingDraw = shared { draws().any { it.pending } }

    val publishedChanges = shared { changeOrders().filter { it.published != null }.map { it._id } }
    val hasPendingChange = shared {
        val published = publishedChanges()
        pendingItemChanges().any { it.pending && it.itemId == lineItemId && it.changeRequest in published }
    }

    val paid = shared {
        val line = lineItem()
        if (line.complete != null) return@shared line.wraps.price
        draws()
            .filter { (it.pending || it.accepted == true) }
            .sumOf { it.amount }
    }

    private val selectedSection = Property(WorkItemSection.Info)

    override fun ViewWriter.renderMainContent(): ViewModifiable = col {
        launch { assertViewerLens(lens) }

        expanding - scrolls - col {
            spacing = AppDimensions.majorColumnSpacing

            lightSection - col {
                title { ::content { lineItem().name } }

                label2("Status") {
                    title {
                        dynamicTheme {
                            LineItemStateSemantic(lineItem().state)
                        }
                        ::content { lineItem().state.displayName }
                    }
                }
            }

            lineItemBanners(lineItem, messages, hasPendingDraw, hasPendingChange, lens, null)

            InnerTabBar.onNext - row {
                for (section in WorkItemSection.entries) {
                    expanding - toggleButton {
                        centered - body(section.displayName)
                        checked bind selectedSection.equalTo(section)
                    }
                }
            }

            stack {
                reactive {
                    clearChildren()
                    when (selectedSection()) {
                        WorkItemSection.Info -> infoSection()
                        WorkItemSection.Ledger -> ledgerSection()
                        WorkItemSection.Tasks -> tasksSection()
                        WorkItemSection.Notes -> notesSection()
                    }
                }
            }
        }

        actionBar()
    }

    @Serializable
    private enum class WorkItemSection(
        override val displayName: String,
    ) : HasDisplayName {
        Info("Info"),
        Ledger("Log"),
        Tasks("Tasks"),
        Notes("Notes"),
    }

    private fun ViewWriter.infoSection() = col {
        labeledSection("Description") {
            body { ::content { lineItem().description } }
        }
    }

    val changes = shared { lineItem().let { it.changes + it.wraps.credits }.sortedBy { it.timestamp } }


    private fun ViewWriter.ledgerSection() = col {
        spacing = AppDimensions.majorColumnSpacing

        labeledSection("Ledger") {
            col {
                keyMoney("Original Price") { lineItem().wraps.originalPrice }
                keyView("Accepted Changes") {
                    renderPriceChange(false) { lineItem().wraps.let { it.price - it.originalPrice } }
                }
                keyView("Active Price") {
                    priceWithChange(
                        addPadding = false,
                        price = { lineItem().wraps.price },
                        change = { lineItem().wraps.let { it.price - it.originalPrice } }
                    )
                }
                keyMoney("Paid") { paid() }
                keyString("% Complete") { (paid().toDouble() / lineItem().wraps.price).renderPercent() }
            }
        }

        onlyWhen { changes().isNotEmpty() || lineItem().changeRequest != null } - changeLog()
    }

    private fun ViewWriter.changeLog() = col {
        spacing = 5.dp

        bodyBold("Changes")

        WorkLogCard.onNext - button {
            row {
                spacing = 5.dp
                centered - bodyBold {
                    ::content { if (lineItem().changeRequest != null) "New" else "Original" }
                }
                expanding - centered - italic - smallBody {
                    ::content { lineItem().changeRequest?.itemNumber ?: "" }
                }
                centered - priceWithChange(
                    addPadding = false,
                    price = { lineItem().wraps.originalPrice },
                    change = { lineItem().let { line -> line.wraps.originalPrice.takeIf { line.changeRequest != null } } }
                )
            }
            ::enabled { lineItem().changeRequest != null }
            onClick {
                val change = lineItem().changeRequest?.id ?: return@onClick
                JumpTo.ChangeItem(lineItemId)
                mainScreenNavigator.navigate(
                    ChangeOrderDetails(projectId, change, lens, this@LineItemDetailsPage)
                )
            }
        }

        ListSemantic.onNext - col {
            forEachUpdating(changes) { item: Readable<WorkLogItem> ->
                WorkLogCard.onNext - button {
                    launch {
                        when (val it = item()) {
                            is Credit -> JumpTo.Credit.newTarget(it, this@button)
                            is ItemChange -> JumpTo.ChangeItem.newTarget(it._id, this@button)
                        }
                    }
                    row {
                        spacing = 5.dp
                        centered - bodyBold { ::content { item().type } }
                        expanding - centered - italic - smallBody { ::content { item().id ?: "" } }
                        centered - renderPriceChange(false) { item().changeAmount }
                    }
                    ::enabled {
                        when (val it = item()) {
                            is Credit -> it.lineItem != null || it.incident != null || it.projectCreditGroup != null
                            is ItemChange -> true
                        }
                    }
                    onClick {
                        when (val c = item()) {
                            is Credit -> {
                                val page = c.lineItem
                                    ?.let { id ->
                                        val line = lineItem()
                                        LineItemCompletionForm.createDialog(line, project(), lens)
                                    }
                                    ?: c.incident?.let { id ->
                                        val session = notNullSession()
                                        val incident = session.incidents[id]() ?: notFoundError<Incident>()
                                        IncidentReportForm.createDialog(
                                            project(),
                                            incident,
                                            session.incidentMessages.query(
                                                Query(limit = Int.MAX_VALUE) { it.incident eq id }
                                            )().sortedBy { it.created },
                                            lens
                                        )
                                    }
                                    ?: project().projectCredits.find { it.projectCreditGroup == c.projectCreditGroup }?.let {
                                        val session = notNullSession()
                                        val project = project()
                                        ProjectCreditDetails(
                                            it,
                                            project.client?.let(session.clients::get)?.await() ?: notFoundError<Client>(),
                                            session.contractors[project.contractor]() ?: notFoundError<Contractor>(),
                                            notNullSession().userId,
                                            project
                                        )
                                    }

                                page?.let(mainScreenNavigator::navigate)
                            }
                            is ItemChange -> {
                                JumpTo.ChangeItem(c._id)
                                mainScreenNavigator.navigate(
                                    ChangeOrderDetails(projectId, c.changeRequest, lens, this@LineItemDetailsPage)
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    private fun ViewWriter.tasksSection() = col {
        val newItems = Property<List<Property<PunchListItem>>>(emptyList())

        launch {
            notNullSession().punchLists.markAsReadUpdateOnRemove(this@col, tasks())
        }

        atEnd - tertiaryButton - button {
            spacing = 0.3.rem
            existsDefaultFalse {
                lens == ProjectLens.Contractor &&
                        project().state.newTasksAllowed() &&
                        lineItem().state.newTasksAllowed()
            }
            ::enabled {
                !project().state.isProjectLocked()
            }
            specCenteredText("+ Work Task")
            onClick {
                newItems.value += Property(
                    PunchListItem(
                        project = project.awaitOnce()._id,
                        lineItem = lineItem()._id,
                        content = "",
                        required = false
                    )
                )
            }
        }

        punchListCol(tasks, newItems, lineItem, lens, allowReorder = true, setTargets = true, expanded = Property(true))
    }

    private fun ViewWriter.notesSection() = col {
        val notes = contractorNotes.shareFilter { it.lineItem == lineItemId }

        launch {
            notNullSession().contractorNotes.markAsReadUpdateOnRemove(this@col, notes())
        }

        if (lens == ProjectLens.Contractor) {
            atEnd - tertiaryButton - button {
                ::exists { !lineItem().cancelled }
                ::enabled { lineItem().state.newNotesAllowed() }
                spacing = 0.3.rem
                specCenteredText("+ Work Note")
                onClick {
                    val it = lineItem()
                    dialogScreenNavigator.navigate(CreateOrEditNote(it.project, it._id, null))
                }
            }
        }

        renderContractorNotes(
            notes,
            groupName = { lineItem().name },
            lens,
        )
    }

    private fun ViewWriter.actionBar(): ViewModifiable {
        val actions = lineItemActions(
            lineItem,
            tasks,
            project,
            messages,
            hasPendingDraw,
            hasPendingChange,
            lens
        )

        return onlyWhen { lineItem().scopeSet != true } - darkSection - mobileButtonRow(actions.toList().reversed())
    }

    data object WorkLogCard : Semantic {
        override val key: String = "wrkcrd"
        override fun default(theme: Theme): ThemeAndBack = theme[LightSection].theme.copy(
            id = key,
            derivations = mapOf(
                DisabledSemantic to { disabled ->
                    disabled.copy(
                        id = "dsbl",
                        derivations = mapOf(
                            HoverSemantic to { it.withoutBack }
                        )
                    ).withoutBack
                }
            )
        ).withBack
    }
}